import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Pencil, RefreshCw, Save, Trash2, X } from "lucide-react";

import { useConsumerAuth } from "../context/ConsumerAuthContext";
import consumerApi from "../lib/consumerApi";
import { premiumPersonalManagerEnabled } from "./PremiumYourOdds";
import PremiumSpending from "./PremiumSpending";
import PremiumEndingToday from "./PremiumEndingToday";

export default function PremiumCompetitionManager() {
  const auth = useConsumerAuth();
  const enabled = premiumPersonalManagerEnabled();
  const entitled = auth?.account?.subscription?.entitlement_active === true;
  if (!enabled) return null;
  if (!auth?.ready) return <p role="status">Loading private records...</p>;
  if (!entitled) return <section><h2 className="font-display text-2xl font-semibold">My competitions</h2><p className="mt-3 text-sm text-white/50">A Premium subscription is required to keep a private ticket and spend record.</p></section>;
  if (!auth.account.user?.user_id) return null;
  return <OwnedManager key={auth.account.user.user_id} auth={auth} />;
}

function OwnedManager({ auth }) {
  const [records, setRecords] = useState(null);
  const [error, setError] = useState("");
  const [working, setWorking] = useState(false);
  const [revision, setRevision] = useState(0);
  const [reload, setReload] = useState(0);
  const [editing, setEditing] = useState(null);
  const [visibleCount, setVisibleCount] = useState(100);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);

  useEffect(() => {
    let active = true;
    setRecords(null);
    setError("");
    consumerApi.get("/premium/personal-competitions").then(
      ({ data }) => { if (active) setRecords(data); },
      () => { if (active) setError("Private records could not be loaded. Please try again later."); },
    );
    return () => { active = false; };
  }, [reload]);

  const remove = async (record) => {
    setWorking(true);
    setError("");
    try {
      await auth.mutate("delete", `/premium/personal-competitions/${encodeURIComponent(record.competition_id)}`);
      if (!alive.current) return;
      setRecords((current) => current.filter((item) => item.entry_id !== record.entry_id));
      setEditing(null);
      setRevision((value) => value + 1);
    } catch { if (alive.current) setError("This record could not be removed. Please try again."); }
    finally { if (alive.current) setWorking(false); }
  };
  const save = async (event, record) => {
    event.preventDefault();
    const tickets = Number(editing.tickets);
    if (!Number.isInteger(tickets) || tickets < 1 || tickets > 100000 ||
        !/^\d+(?:\.\d{1,2})?$/.test(editing.amount) || Number(editing.amount) > 1000000) {
      setError("Enter 1 to 100,000 tickets and a valid amount with at most two decimal places.");
      return;
    }
    setWorking(true); setError("");
    try {
      const data = await auth.mutate("put", `/premium/personal-competitions/${encodeURIComponent(record.competition_id)}`, {
        tickets_bought: tickets, amount_spent: Number(editing.amount).toFixed(2),
      });
      if (!alive.current) return;
      setRecords((current) => current.map((item) => item.entry_id === record.entry_id ? { ...item, ...data } : item));
      setEditing(null); setRevision((value) => value + 1);
    } catch { if (alive.current) setError("This record could not be saved. Please try again."); }
    finally { if (alive.current) setWorking(false); }
  };
  return (
    <section data-testid="premium-competition-manager">
      <h2 className="font-display text-2xl font-semibold">My competitions</h2>
      <p className="mt-2 text-sm text-[var(--cc-text-muted)]">Your private ticket record. Recorded spend is informational and is never a recommendation to spend more.</p>
      <PremiumSpending revision={revision} />
      <PremiumEndingToday key={revision} />
      {error && <div className="mt-4 flex items-center gap-3"><p role="alert" className="text-sm">{error}</p>{records === null && <button type="button" aria-label="Retry private records" title="Retry private records" className="min-h-10 min-w-10 rounded border border-white/15 p-2" onClick={() => setReload((value) => value + 1)}><RefreshCw size={18} /></button>}</div>}
      <ul className="divide-y divide-white/10">
        {(records || []).slice(0, visibleCount).map((record) => (
          <li className="flex flex-wrap items-center justify-between gap-4 py-5" key={record.entry_id}>
            <div><Link className="font-medium text-[var(--cc-gold)]" to={record.competition.slug ? `/competition/${record.competition.slug}` : "/competitions"}>{record.competition.title}</Link><p className="mt-1 text-sm text-white/50">{record.tickets_bought} tickets · £{record.amount_spent}</p></div>
            <div className="flex gap-2">
              <button disabled={working} aria-label={`Edit ${record.competition.title}`} title={`Edit ${record.competition.title}`} className="min-h-10 min-w-10 rounded border border-white/15 p-2" type="button" onClick={() => { setError(""); setEditing({ id: record.entry_id, tickets: String(record.tickets_bought), amount: record.amount_spent }); }}><Pencil size={16} /></button>
              <button disabled={working} aria-label={`Remove ${record.competition.title}`} title={`Remove ${record.competition.title}`} className="min-h-10 min-w-10 rounded border border-white/15 p-2" type="button" onClick={() => remove(record)}><Trash2 size={16} /></button>
            </div>
            {editing?.id === record.entry_id && <form className="flex w-full flex-wrap items-end gap-3" onSubmit={(event) => save(event, record)}>
              <label className="text-sm">Tickets<input aria-label={`Tickets for ${record.competition.title}`} className="mt-1 block min-h-10 w-32 rounded border border-white/20 bg-[var(--cc-ink)] p-2" type="number" min="1" max="100000" step="1" required disabled={working} value={editing.tickets} onChange={(event) => setEditing({ ...editing, tickets: event.target.value })} /></label>
              <label className="text-sm">Recorded spend (GBP)<input aria-label={`Recorded spend for ${record.competition.title}`} className="mt-1 block min-h-10 w-40 rounded border border-white/20 bg-[var(--cc-ink)] p-2" type="number" min="0" max="1000000" step="0.01" required disabled={working} value={editing.amount} onChange={(event) => setEditing({ ...editing, amount: event.target.value })} /></label>
              <button type="submit" disabled={working} aria-label={`Save ${record.competition.title}`} title="Save record" className="min-h-10 min-w-10 rounded border border-white/15 p-2"><Save size={18} /></button>
              <button type="button" disabled={working} aria-label="Cancel editing" title="Cancel editing" className="min-h-10 min-w-10 rounded border border-white/15 p-2" onClick={() => setEditing(null)}><X size={18} /></button>
            </form>}
          </li>
        ))}
        {!records?.length && !error && <li className="py-6 text-sm text-white/45">{records ? "No competitions recorded yet." : "Loading private records..."}</li>}
      </ul>
      {records?.length > visibleCount && <button type="button" className="mt-3 min-h-10 rounded border border-white/15 px-3 text-sm" onClick={() => setVisibleCount((value) => value + 100)}>Load more records</button>}
    </section>
  );
}
